import type { GameStateData, AgentState, TaskState, DifficultyParams, KPIs, GamePhase } from '../types';
import { RULES, ECONOMY, SCORING } from '../constants';

function createDefaultDifficulty(): DifficultyParams {
  return {
    taskSpawnInterval: 4,
    agentDriftSpeed: 0.02,
    errorSubtlety: 0,
    taskComplexity: 4,
    queueMaxSize: 8,
    taskTimeout: 15,
    priorityChance: 0,
    correctionCooldown: 3,
    agentSpawnCost: ECONOMY.spawnCostBase,
  };
}

function createDefaultKPIs(): KPIs {
  return { dr: 100, vs: 100, ce: 100, ta: 100, apm: 100 };
}

export class GameState implements GameStateData {
  phase: GamePhase = 'start';
  time = 0;
  lives = RULES.maxLives;
  compute: number = ECONOMY.startingCompute;
  combo = 0;
  agents: AgentState[] = [];
  taskQueue: TaskState[] = [];
  activeValidation: GameStateData['activeValidation'] = null;
  kpis: KPIs = createDefaultKPIs();
  score = 0;
  peakAgents = 0;
  difficulty: DifficultyParams = createDefaultDifficulty();

  // KPI tracking internals
  tasksAssigned = 0;
  tasksExpired = 0;
  totalTimeAbove75Drift = 0;
  optimalCorrections = 0;
  totalCorrections = 0;
  correctDecisions = 0;
  totalDecisions = 0;

  // Track total agents spawned for cost calculation
  totalAgentsSpawned = 0;

  getNextSpawnCost(): number {
    const count = this.agents.length;
    return Math.floor(
      ECONOMY.spawnCostBase * Math.pow(ECONOMY.spawnCostMultiplier, count)
    );
  }

  canSpawnAgent(): boolean {
    return this.compute >= this.getNextSpawnCost() && this.agents.length < 10;
  }

  updateAPM(): void {
    const w = SCORING.apmWeights;
    this.kpis.apm = Math.round(
      this.kpis.dr * w.dr +
      this.kpis.vs * w.vs +
      this.kpis.ce * w.ce +
      this.kpis.ta * w.ta
    );
  }

  updateDR(): void {
    const total = this.tasksAssigned + this.tasksExpired;
    this.kpis.dr = total > 0
      ? Math.round((this.tasksAssigned / total) * 100)
      : 100;
  }

  updateVS(): void {
    this.kpis.vs = this.time > 0
      ? Math.round((1 - this.totalTimeAbove75Drift / this.time) * 100)
      : 100;
  }

  updateCE(): void {
    this.kpis.ce = this.totalCorrections > 0
      ? Math.round((this.optimalCorrections / this.totalCorrections) * 100)
      : 100;
  }

  updateTA(): void {
    this.kpis.ta = this.totalDecisions > 0
      ? Math.round((this.correctDecisions / this.totalDecisions) * 100)
      : 100;
  }

  getFinalScore(): number {
    return Math.round(this.kpis.apm * this.time * Math.max(1, this.peakAgents));
  }

  reset(): void {
    this.phase = 'start';
    this.time = 0;
    this.lives = RULES.maxLives;
    this.compute = ECONOMY.startingCompute;
    this.combo = 0;
    this.agents = [];
    this.taskQueue = [];
    this.activeValidation = null;
    this.kpis = createDefaultKPIs();
    this.score = 0;
    this.peakAgents = 0;
    this.difficulty = createDefaultDifficulty();
    this.tasksAssigned = 0;
    this.tasksExpired = 0;
    this.totalTimeAbove75Drift = 0;
    this.optimalCorrections = 0;
    this.totalCorrections = 0;
    this.correctDecisions = 0;
    this.totalDecisions = 0;
    this.totalAgentsSpawned = 0;
  }
}
